import { formatMoney } from 'global';

// Lets customers fill a gift box from a dropdown and add it to the cart as one group:
// the gift box product is the parent line and the chosen items are nested cart lines.
const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

const getCart = async () => (await fetch(`${root}cart.js`)).json();

const boxNumbers = (cart) => cart.items
  .filter((item) => item.properties && item.properties._giftbox_role === 'box')
  .map((item) => parseInt(item.properties._giftbox_no, 10))
  .filter((n) => !Number.isNaN(n));

class GiftBoxBuilder extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-gbb-json]').textContent);
    this.items = new Map();
    this.el = (name) => this.querySelector(`[data-gbb-${name}]`);

    this.el('select').addEventListener('change', () => this.toggleAdd());
    this.el('add').addEventListener('click', () => this.addItem());
    this.el('submit').addEventListener('click', () => this.submit());
    this.el('again').addEventListener('click', () => this.reset());
    this.el('list').addEventListener('click', (e) => this.onListClick(e));

    this.render();
    this.updateBoxCount();
    document.addEventListener('cart:change', () => this.updateBoxCount());
  }

  get count() {
    let total = 0;
    this.items.forEach((qty) => { total += qty; });
    return total;
  }

  money(cents) {
    return formatMoney(cents);
  }

  showError(message) {
    const error = this.el('error');
    error.textContent = message || '';
    error.hidden = !message;
  }

  toggleAdd() {
    this.el('add').disabled = !this.el('select').value;
    this.showError('');
  }

  // Quantity a variant can still take, respecting stock and the per-box limit
  setQty(id, qty) {
    const variant = this.data.variants[id];
    const others = this.count - (this.items.get(id) || 0);
    let next = Math.max(0, qty);
    if (variant.max !== null && next > variant.max) {
      next = variant.max;
      this.showError(`Only ${variant.max} of ${variant.title} available.`);
    }
    if (this.data.max > 0 && others + next > this.data.max) {
      next = Math.max(0, this.data.max - others);
      this.showError(`A gift box holds up to ${this.data.max} items.`);
    }
    if (next === 0) this.items.delete(id);
    else this.items.set(id, next);
  }

  addItem() {
    const id = this.el('select').value;
    if (!id || !this.data.variants[id]) return;
    const qty = Math.max(1, parseInt(this.el('qty').value, 10) || 1);
    this.showError('');
    this.setQty(id, (this.items.get(id) || 0) + qty);
    this.el('select').value = '';
    this.el('qty').value = 1;
    this.toggleAdd();
    this.render();
  }

  onListClick(e) {
    const button = e.target.closest('button[data-action]');
    if (!button) return;
    const id = button.closest('[data-id]').dataset.id;
    const current = this.items.get(id) || 0;
    this.showError('');
    if (button.dataset.action === 'plus') this.setQty(id, current + 1);
    if (button.dataset.action === 'minus') this.setQty(id, current - 1);
    if (button.dataset.action === 'remove') this.setQty(id, 0);
    this.render();
  }

  render() {
    const list = this.el('list');
    let subtotal = 0;
    list.innerHTML = '';
    this.items.forEach((qty, id) => {
      const v = this.data.variants[id];
      subtotal += v.price * qty;
      const li = document.createElement('li');
      li.className = 'gbb__item';
      li.dataset.id = id;
      li.innerHTML = `
        <span class="gbb__item-img">${v.image ? `<img src="${v.image}" alt="" width="64" height="64" loading="lazy">` : ''}</span>
        <span class="gbb__item-info">
          <span class="gbb__item-title"></span>
          <span class="gbb__item-variant hdt-s-text2"></span>
          <span class="gbb__item-price hdt-s-text2">${this.money(v.price * qty)}</span>
        </span>
        <span class="gbb__item-qty">
          <button type="button" data-action="minus" aria-label="Decrease">−</button>
          <span>${qty}</span>
          <button type="button" data-action="plus" aria-label="Increase">+</button>
        </span>
        <button type="button" class="gbb__item-remove" data-action="remove" aria-label="Remove">×</button>`;
      li.querySelector('.gbb__item-title').textContent = v.title;
      li.querySelector('.gbb__item-variant').textContent = v.variant || '';
      list.appendChild(li);
    });

    this.el('empty').hidden = this.items.size > 0;
    this.el('subtotal').textContent = this.money(subtotal);
    this.el('total').textContent = this.money(subtotal + this.data.feePrice);
    this.el('submit').disabled = this.count < this.data.min;
  }

  async updateBoxCount() {
    try {
      const cart = await getCart();
      const boxes = boxNumbers(cart).length;
      const note = this.el('in-cart');
      note.textContent = boxes ? `You have ${boxes} gift box${boxes > 1 ? 'es' : ''} in your bag.` : '';
      note.hidden = !boxes;
      const next = boxes ? Math.max(...boxNumbers(cart)) + 1 : 1;
      this.el('box-title').textContent = `${this.data.label} #${next}`;
    } catch (e) { /* the count is only informational */ }
  }

  async submit() {
    if (this.count < this.data.min) return;
    const button = this.el('submit');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.showError('');

    try {
      const cart = await getCart();
      const numbers = boxNumbers(cart);
      const no = numbers.length ? Math.max(...numbers) + 1 : 1;
      const label = `${this.data.label} #${no}`;
      const boxProperties = { 'Gift Box': label, _giftbox_no: String(no), _giftbox_role: 'box' };
      const message = this.el('message') ? this.el('message').value.trim() : '';
      if (message) boxProperties[this.data.messageKey] = message;

      // The box is the parent line; items are nested under it with parent_id, so Shopify
      // keeps them grouped and removes them when the box is removed
      const items = [{ id: this.data.fee, quantity: 1, properties: boxProperties }];
      this.items.forEach((quantity, id) => {
        items.push({ id: Number(id), quantity, parent_id: this.data.fee, properties: { 'Gift Box': label } });
      });

      // Ask the cart drawer / cart page which sections to re-render, like the theme's own forms
      const sections = [];
      document.dispatchEvent(new CustomEvent('cart:push:section.id', { bubbles: true, detail: { sections } }));

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || 'Could not add the gift box.');

      const updated = await getCart();
      updated.sections = json.sections;
      document.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: updated },
      }));

      this.el('done-title').textContent = `${label} has been added to your bag.`;
      this.el('done').hidden = false;
      this.querySelector('.gbb__grid').hidden = true;
    } catch (error) {
      this.showError(error.message);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = this.count < this.data.min;
    }
  }

  reset() {
    this.items.clear();
    if (this.el('message')) this.el('message').value = '';
    this.el('done').hidden = true;
    this.querySelector('.gbb__grid').hidden = false;
    this.render();
    this.updateBoxCount();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('gift-box-builder')) customElements.define('gift-box-builder', GiftBoxBuilder);
